import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Typography, Paper, Snackbar, Alert, CircularProgress, Box, Button } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { eventService } from '../services';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import EventFormContainer, { EventFormData } from '../../../features/event-form-feature/EventFormContainer';
import { mapApiEventToFormData } from '../../../features/event-form-feature/mapApiEventToFormData';

const AdminEditEventPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [eventData, setEventData] = useState<Partial<EventFormData> | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const { data: eventResponse, isLoading: isLoadingEvent, error: queryError } = useQuery({
    queryKey: ['admin-event', eventId],
    queryFn: async () => {
      if (!eventId) {
        throw new Error('Event ID não fornecido');
      }
      return eventService.getById(eventId);
    },
    enabled: !!eventId,
    retry: 1,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const updateEventMutation = useMutation({
    mutationFn: (data: FormData) => {
      if (!eventId) throw new Error('Event ID não fornecido');
      return eventService.update(eventId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['admin-event', eventId] });
      setSuccess('Evento atualizado com sucesso!');
      setTimeout(() => {
        navigate('/events');
      }, 2000);
    },
    onError: (error: any) => {
      setError(error.response?.data?.message || 'Erro ao atualizar evento');
    },
  });

  const applyEventResponse = useCallback((event: Record<string, any>) => {
    setEventData(mapApiEventToFormData(event));
    setLoading(false);
  }, []);

  useEffect(() => {
    if (eventResponse?.data) {
      try {
        applyEventResponse(eventResponse.data as Record<string, any>);
      } catch {
        setError('Erro ao processar dados do evento');
        setLoading(false);
      }
    } else if (eventResponse && !eventResponse.data) {
      setError('Evento não encontrado');
      setLoading(false);
    }
  }, [eventResponse, applyEventResponse]);

  const reloadEventData = useCallback(async () => {
    if (!eventId) return;
    const response = await eventService.getById(eventId);
    if (response?.data) {
      applyEventResponse(response.data as Record<string, any>);
    }
  }, [eventId, applyEventResponse]);

  const handleEventSubmit = async (data: EventFormData) => {
    try {
      setError(null);
      setSuccess(null);

      const formData = new FormData();
      const { coverImage, galleryImages, ...dataWithoutImages } = data;

      const eventDataToSend = {
        ...dataWithoutImages,
        startDate: data.startDate?.toISOString(),
        endDate: data.endDate?.toISOString(),
        registrationDeadline: data.registrationDeadline?.toISOString(),
        coverImage: typeof coverImage === 'string' ? coverImage : undefined,
        galleryImages: (galleryImages || []).filter(
          (img): img is string => typeof img === 'string' && img.length > 0,
        ),
        tickets: data.tickets?.map((t) => ({
          ...t,
          startSaleDate: t.startSaleDate?.toISOString(),
          endSaleDate: t.endSaleDate?.toISOString(),
        })),
        ticketTypes: data.ticketTypes?.map((type) => ({
          ...type,
          batches: type.batches?.map((batch) => ({
            ...batch,
            startSaleDate: batch.startSaleDate?.toISOString(),
            endSaleDate: batch.endSaleDate?.toISOString(),
          })),
        })),
        salesPhases: data.salesPhases?.map((phase) => ({
          ...phase,
          startSaleDate: phase.startSaleDate?.toISOString(),
          endSaleDate: phase.endSaleDate?.toISOString(),
        })),
        products: data.products?.map((p) => ({
          ...p,
          startSaleDate: p.startSaleDate?.toISOString(),
          endSaleDate: p.endSaleDate?.toISOString(),
        })),
      };

      formData.append('eventData', JSON.stringify(eventDataToSend));

      if (data.coverImage instanceof File) {
        formData.append('coverImage', data.coverImage);
      }

      if (data.galleryImages && data.galleryImages.length > 0) {
        data.galleryImages.forEach((img) => {
          if (img instanceof File) {
            formData.append('galleryImages', img);
          }
        });
      }

      if (data.products) {
        data.products.forEach((product) => {
          if (product.image instanceof File) {
            formData.append('productImages', product.image);
          }
        });
      }

      await updateEventMutation.mutateAsync(formData);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Erro ao atualizar evento');
    }
  };

  const handleSaveDraft = async (data: EventFormData) => {
    await handleEventSubmit({ ...data, status: 'paused' });
  };

  const handleCloseAlert = () => {
    setError(null);
    setSuccess(null);
  };

  useEffect(() => {
    if (queryError) {
      setError('Erro ao carregar evento');
      setLoading(false);
    }
  }, [queryError]);

  return (
    <Paper elevation={3} sx={{ p: 4, m: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/events')}
            variant="outlined"
          >
            Voltar
          </Button>
          <Typography variant="h4" component="h1">
            Editar Evento
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Event ID: {eventId || 'NÃO ENCONTRADO'}
        </Typography>
      </Box>

      {isLoadingEvent || loading ? (
        <Box textAlign="center" sx={{ py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Carregando evento...
          </Typography>
        </Box>
      ) : queryError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>Erro ao carregar evento</Typography>
          <Typography variant="body2">
            {queryError instanceof Error ? queryError.message : 'Erro desconhecido'}
          </Typography>
        </Alert>
      ) : !eventResponse?.data ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Evento não encontrado. Verifique se o ID está correto.
        </Alert>
      ) : !eventData ? (
        <Box textAlign="center" sx={{ py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Preparando formulário...
          </Typography>
        </Box>
      ) : (
        <EventFormContainer
          initialData={eventData}
          onSubmit={handleEventSubmit}
          onSaveDraft={handleSaveDraft}
          onReloadEvent={reloadEventData}
        />
      )}

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default AdminEditEventPage;
